import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePopupManager } from './PopupManager';
import ExitIntentPopup from './ExitIntentPopup';
import FeedbackPopup from './FeedbackPopup';
import TrafficSourceModal from './TrafficSourceModal';
import {
  FEEDBACK_ENGAGEMENT_SECONDS,
  FEEDBACK_MIN_PAGE_VIEWS,
  TRAFFIC_SOURCE_DELAY_SECONDS,
  EXIT_INTENT_MIN_DELAY_AFTER_TRAFFIC_SECONDS,
} from '../config/siteConfig';

const canShowExitIntent = () => {
  if (sessionStorage.getItem('exitPopupShown')) return false;
  if (!sessionStorage.getItem('trafficSourceAnswered')) return false;

  const dismissedAt = parseInt(sessionStorage.getItem('trafficDismissedAt') || '0', 10);
  if (!dismissedAt) return false;
  if (Date.now() - dismissedAt < EXIT_INTENT_MIN_DELAY_AFTER_TRAFFIC_SECONDS * 1000) {
    return false;
  }

  return true;
};

const PopupOrchestrator = () => {
  const { tryRequestPopup, canRequestPopup } = usePopupManager();
  const location = useLocation();
  const pageViews = useRef(0);
  const exitEnabledRef = useRef(false);
  const tabHiddenAtRef = useRef(null);

  useEffect(() => {
    pageViews.current += 1;
    sessionStorage.setItem('pageViewCount', String(pageViews.current));
  }, [location.pathname]);

  // Traffic source — once, 10s after landing
  useEffect(() => {
    if (sessionStorage.getItem('trafficSourceAnswered')) return;

    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('trafficSourceAnswered')) {
        tryRequestPopup('traffic');
      }
    }, TRAFFIC_SOURCE_DELAY_SECONDS * 1000);

    return () => clearTimeout(timer);
  }, [tryRequestPopup]);

  // Enable exit-intent only after traffic modal is fully dismissed + delay
  useEffect(() => {
    const interval = setInterval(() => {
      exitEnabledRef.current = canShowExitIntent() && canRequestPopup();
    }, 1000);
    return () => clearInterval(interval);
  }, [canRequestPopup]);

  // Exit intent — desktop: mouse leaves toward browser chrome; mobile: tab switch / minimize
  useEffect(() => {
    const handleMouseOut = (e) => {
      if (!exitEnabledRef.current) return;
      if (e.clientY > 10) return;
      if (e.relatedTarget != null) return;
      if (tryRequestPopup('exit')) {
        exitEnabledRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (!canShowExitIntent()) return;

      if (document.visibilityState === 'hidden') {
        tabHiddenAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState !== 'visible' || !tabHiddenAtRef.current) return;

      const hiddenFor = Date.now() - tabHiddenAtRef.current;
      tabHiddenAtRef.current = null;

      // User switched tab or minimized for at least 1s — treat as leaving
      if (hiddenFor >= 1000 && exitEnabledRef.current && canRequestPopup()) {
        if (tryRequestPopup('exit')) {
          exitEnabledRef.current = false;
        }
      }
    };

    document.documentElement.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.documentElement.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tryRequestPopup, canRequestPopup]);

  // Feedback — only after traffic answered, then engagement delay from that moment
  useEffect(() => {
    if (sessionStorage.getItem('feedbackShown')) return;

    let feedbackTimer;

    const scheduleFeedback = () => {
      if (sessionStorage.getItem('feedbackShown')) return;
      if (!sessionStorage.getItem('trafficSourceAnswered')) return;

      const dismissedAt = parseInt(sessionStorage.getItem('trafficDismissedAt') || '0', 10);
      const waitMs = dismissedAt
        ? Math.max(0, dismissedAt + FEEDBACK_ENGAGEMENT_SECONDS * 1000 - Date.now())
        : FEEDBACK_ENGAGEMENT_SECONDS * 1000;

      feedbackTimer = setTimeout(() => {
        const views = parseInt(sessionStorage.getItem('pageViewCount') || '1', 10);
        if (views >= FEEDBACK_MIN_PAGE_VIEWS) {
          tryRequestPopup('feedback');
        }
      }, waitMs);
    };

    const poll = setInterval(() => {
      if (sessionStorage.getItem('trafficSourceAnswered') && sessionStorage.getItem('trafficDismissedAt')) {
        clearInterval(poll);
        scheduleFeedback();
      }
    }, 1000);

    return () => {
      clearInterval(poll);
      if (feedbackTimer) clearTimeout(feedbackTimer);
    };
  }, [tryRequestPopup]);

  return (
    <>
      <ExitIntentPopup />
      <FeedbackPopup />
      <TrafficSourceModal />
    </>
  );
};

export default PopupOrchestrator;
